import React, { useState, useCallback, useMemo, useRef } from 'react';
import { translate } from '../Services/translation';
import { toggleScrollLock } from '../Services/helpers';
import { Plus } from './Icons/Plus';
import { Minus } from './Icons/Minus';
import { Cross } from './Icons/Cross';

const ImageHotspot = ({ title, preamble, properties, imageSrc, imageMobileSrc, hotspots, linkUrl, linkText, usps }) => {
    const [activeHotspotId, setActiveHotspotId] = useState(null);
    const hotspotsRef = useRef(null);
    const propertiesList = useMemo(() => JSON.parse(properties), [properties]);
    const hotspotsList = useMemo(() => JSON.parse(hotspots), [hotspots]);
    const uspsList = useMemo(() => JSON.parse(usps), [usps]);

    return (
        <div className="image-hotspot">
            <div className="image-hotspot__outer">
                <div className="image-hotspot__inner">
                    
                    <div className="image-hotspot__info">
                        <h2 className="image-hotspot__title">{title}</h2>
                        <span className="image-hotspot__preamble">{preamble}</span>
                    </div>

                    <ul className="image-hotspot__properties" data-aos="fade" data-aos-easing="linear" data-aos-offset="100" data-aos-duration="400">
                        {propertiesList.map((item, index) => (
                            <li key={index} className="image-hotspot-property">
                                {item.iconSrc && <img src={item.iconSrc} alt={item.iconText} className="image-hotspot-property__icon" loading='lazy' />}
                                <div className='image-hotspot-property__text' dangerouslySetInnerHTML={{ __html: item.iconText }}></div>
                            </li>
                        ))}
                    </ul>

                    <div className="image-hotspot__hotspot">

                        <picture className='image-hotspot__media'>
                            <source media="(min-width:768px)" srcSet={imageSrc} />
                            <img src={imageMobileSrc} alt={title + preamble}  data-aos="fade" data-aos-easing="linear" data-aos-offset="100" data-aos-duration="400" />

                            <ul className="image-hotspot__hotspots" ref={hotspotsRef}>
                                {hotspotsList.map((item, index) => 
                                    <ImageHotspotItem key={index} {...{ ...item, activeHotspotId, setActiveHotspotId, hotspotsRef }} />
                                )}
                            </ul>
                        </picture>
                    </div>

                    <div className="image-hotspot__link">
                        {linkUrl && <a href={linkUrl} className="button button--big">{linkText}</a>}
                    </div>

                    <ul className="image-hotspot__usps" data-aos="fade" data-aos-easing="linear" data-aos-offset="100" data-aos-duration="400">
                        {uspsList.map((item, index) => (
                            <li key={index} className="image-hotspot-usp">
                                {item.title && <h3 className="image-hotspot-usp__title">{item.title}</h3>}
                                {item.text && <div className="image-hotspot-usp__text" dangerouslySetInnerHTML={{ __html: item.text }}></div>}
                            </li>
                        ))}
                    </ul>

                </div>
            </div>
        </div>
    );
}

const ImageHotspotItem = ({ coordinateXMobile, coordinateYMobile, coordinateX, coordinateY, imageSrc, imageAlt, text, activeHotspotId, setActiveHotspotId, hotspotsRef }) => {
    const id = useMemo(() => `image-hotspot-item-${coordinateXMobile}-${coordinateYMobile}-${coordinateX}-${coordinateY}`, []);
    const open = activeHotspotId == id;
    const buttonRef = useRef(null);
    const dialogRef = useRef(null);
    const [direction, setDirection] = useState('ltr');
    const [position, setPosition] = useState('bottom');

    const getDirectionDesktop = useCallback(() => {
        // Gets the X left/right position of the dialog
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dialogRect = dialogRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;    
        const spaceRight = viewportWidth - buttonRect.right;
        const spaceLeft = buttonRect.left;
        const width = dialogRect.width + 32;

        // To the right
        if (spaceRight < spaceLeft) {
            if (spaceRight < width) {
                return setDirection('rtl');
            }
            else {
                return setDirection('ltr');
            }
        }
        // To the left
        else {
            if (spaceLeft < width) {
                return setDirection('ltr');
            }
            else {
                return setDirection('rtl');
            }
        }
    }, []);

    const getPositionDesktop = useCallback(() => {
        // Gets the Y top/center/bottom position of the dialog
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dialogRect = dialogRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;    
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top - 133; // 133 is the height of the header in desktop mode
        const height = dialogRect.height + 32;
    
        if (spaceBelow < height) {
            if (spaceAbove < height) {
                setPosition('center'); // If there is no space above or below, center the dialog
            } 
            else {
                setPosition('top');
            }
        } 
        else {
            setPosition('bottom');
        }
    }, []);

    const toggleHotspot = useCallback(() => {
        // Close every other hotspot
        hotspotsRef.current.querySelectorAll('dialog').forEach(dialog => {
            if (dialog !== dialogRef.current) {
                dialog.close();
            }
        })

        if (open) {
            setActiveHotspotId(null)
            toggleScrollLock(false)
            dialogRef.current.close()
        }
        else {
            setActiveHotspotId(id)
    
            if (dialogRef.current.hasAttribute('open')) {
                dialogRef.current.close()
                toggleScrollLock(false)
            }
            else {
                dialogRef.current.show()
                getPositionDesktop()
                getDirectionDesktop()
                toggleScrollLock(true, true)
            } 
        }

    }, [activeHotspotId, setActiveHotspotId]);

    return (
        <li className={`
            image-hotspot-item ${open ? 'image-hotspot-item--open' : ''}
            image-hotspot-item--${direction}
        `}
            style={{ 
                '--x-mobile': `${coordinateXMobile}%`,
                '--y-mobile': `${coordinateYMobile}%`,
                '--x-desktop': `${coordinateX}%`,
                '--y-desktop': `${coordinateY}%`,
            }}>

            <button 
                ref={buttonRef}
                className="image-hotspot-item__button" 
                aria-controls={id} 
                onClick={() => toggleHotspot()}>
                {open ? <Minus /> : <Plus />}
            </button>

            <dialog
                id={id}
                ref={dialogRef}
                className={`
                    image-hotspot-item__dialog 
                    image-hotspot-item__dialog--${position}`}>

                <div className='image-hotspot-item__dialog-header'>
                    <button 
                        className="image-hotspot-item__dialog-close" 
                        aria-controls={id}
                        onClick={() => toggleHotspot()}>
                        {translate('navigation.close')}
                        <Cross />
                    </button>
                </div>

                {imageSrc && (
                    <img
                        src={imageSrc}
                        alt={imageAlt}
                        className="image-hotspot-item__image"
                        loading='lazy'/> // To prevent height change on open
                )}

                <div
                    className="image-hotspot-item__text"
                    dangerouslySetInnerHTML={{ __html: text }}>
                </div>
            </dialog>

            <div className={`
                image-hotspot-item__backdrop
                ${open 
                    ? 'image-hotspot-item__backdrop--open' 
                    : 'image-hotspot-item__backdrop--closed'}
                `}
                onClick={() => toggleHotspot()}>
            </div>
        </li>
    )
};



export default ImageHotspot;